import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useChatWs } from '../chat/useChatWs';
import * as chatApi from '../../api/chat';
import { tasksApi } from '../../hrms/api/tasks';
import {
  bulkHydrate as bulkHydrateChat,
  incrementForConversation,
  clearConversation,
} from '../../redux/slices/chatUnread';
import {
  bulkHydrate as bulkHydrateTasks,
  pushNotification,
} from '../../redux/slices/notifications';
import { useAuth } from '../../contexts/AuthContext';
import { buildChatUnreadEntries } from './buildChatUnreadEntries';

export function useGlobalNotificationsWs() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const location = useLocation();
  const currentUserId = user?.id ?? '';

  // Track the currently-open conversation so we don't increment unread for it.
  // Chat.tsx reads `?open=<id>` from the URL on mount, so the same param tells
  // us which conversation is being viewed.
  const activeConvIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (location.pathname !== '/chat') {
      activeConvIdRef.current = null;
      return;
    }
    const params = new URLSearchParams(location.search);
    activeConvIdRef.current = params.get('open');
  }, [location.pathname, location.search]);

  // Initial hydration. Runs once per mount (sidebar is mounted for the whole
  // app lifetime, so once per app load is correct).
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;

    (async () => {
      try {
        const convs = await chatApi.listConversations();
        if (cancelled) return;
        dispatch(bulkHydrateChat(buildChatUnreadEntries(convs, currentUserId)));
      } catch {
        // Leave slice empty; bell shows 0 until a frame arrives.
      }
      try {
        const resp = await tasksApi.getNotifications(true, 50);
        if (cancelled) return;
        const items = Array.isArray(resp?.data) ? resp.data : resp?.data?.items ?? [];
        dispatch(bulkHydrateTasks(items));
      } catch {
        // Leave slice empty.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUserId, dispatch]);

  // The second WS connection. Token is read inside useChatWs from storage.
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

  useChatWs(token, (evt: any) => {
    switch (evt?.type) {
      case 'task_notification': {
        dispatch(
          pushNotification({
            id: evt.notification_id,
            task_id: evt.task_id,
            user_id: '',
            kind: evt.kind,
            title: evt.title,
            body: evt.body || '',
            is_read: false,
            read_at: null,
            email_sent: false,
            email_sent_at: null,
            created_at: evt.created_at,
          })
        );
        break;
      }
      case 'new_message': {
        const msg = evt.message;
        if (!msg) break;
        // Suppress increments for the currently-viewed conversation.
        if (msg.conversation_id === activeConvIdRef.current) break;
        // Suppress increments for the user's own messages.
        if (msg.sender_id === currentUserId) break;
        dispatch(
          incrementForConversation({
            conversationId: msg.conversation_id,
            lastActivity: msg.created_at ?? new Date().toISOString(),
            name: msg.conversation_name,
            avatarUrl: msg.conversation_avatar_url ?? null,
            content: msg.content ?? null,
          })
        );
        break;
      }
      case 'seen_receipt': {
        // Server tells us our own user has read this conversation.
        if (evt.user_id !== currentUserId) break;
        if (!evt.conversation_id) break;
        dispatch(clearConversation(evt.conversation_id));
        break;
      }
      default:
        break;
    }
  });
}
